import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import authStorage from '../store/authStorage';

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    // Check for existing token on mount
    const token = authStorage.getToken();
    if (token) {
      // Decode JWT to get user info (without calling backend)
      try {
        const payload = JSON.parse(atob(token.split('.')[1]));
        setUser({
          id: payload.user_id,
          email: payload.email,
          role: payload.role,
        });
      } catch {
        authStorage.removeToken();
      }
    }
    setLoading(false);
  }, []);

  const login = useCallback((token, userData) => {
    authStorage.setToken(token);
    setUser(userData || (() => {
      try {
        const payload = JSON.parse(atob(token.split('.')[1]));
        return { id: payload.user_id, email: payload.email, role: payload.role };
      } catch {
        return null;
      }
    })());
  }, []);

  const logout = useCallback(() => {
    authStorage.removeToken();
    setUser(null);
    navigate('/login');
  }, [navigate]);

  const isAuthenticated = !!user;
  const isAdmin = user?.role === 'ADMIN';
  const isContributor = user?.role === 'CONTRIBUTOR' || user?.role === 'ADMIN';

  return (
    <AuthContext.Provider value={{ user, loading, isAuthenticated, isAdmin, isContributor, login, logout }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}

export default AuthContext;